import { z } from 'zod';
import { RecordIdSchema } from './identifiers';
import { DecimalStringSchema, MoneySchema } from './money';
import { isPercentage } from './settings';
import { BusinessDateSchema, InstantSchema } from './time';
import { OutgoingPaymentMethodSchema } from './treasury';

/**
 * Contract-linked commissions (SALE-COMM-001…003, BD-14).
 *
 * The rules are the client's configuration — nothing is assumed: a rule names who earns (the
 * contract's sales owner, or one payee such as a broker), the percentage of the contract price, the
 * share of the price that must be collected before it may be paid, the dates it applies to, and what
 * an assignment does to it. A commission is **calculated** when the contract is activated, **approved**
 * by someone other than its beneficiary, **payable** once enough is collected, and **paid** only by a
 * treasury payment someone else executes. A cancellation reverses what was not paid and claws back
 * what was; a unit replacement recalculates on the new price.
 */
export const COMMISSION_BENEFICIARIES = ['salesOwner', 'payee'] as const;
export const COMMISSION_RULE_STATES = ['active', 'superseded', 'retired'] as const;
export const SALES_COMMISSION_KINDS = ['commission', 'adjustment', 'clawback'] as const;
export const SALES_COMMISSION_STATES = [
  'calculated',
  'approved',
  'paymentRequested',
  'paid',
  'reversed',
  /** A clawback (or negative adjustment) recorded as recovered, with its reference. */
  'recovered',
] as const;

const PercentSchema = DecimalStringSchema.refine(isPercentage, { message: 'RATE_OUT_OF_RANGE' });

export const CommissionRuleSchema = z.strictObject({
  ruleId: RecordIdSchema,
  version: z.number().int().positive(),
  state: z.enum(COMMISSION_RULE_STATES),
  name: z.string().max(120),
  legalEntityId: RecordIdSchema,
  /** Empty: every project of the legal entity. */
  projectIds: z.array(RecordIdSchema).max(200),
  beneficiary: z.enum(COMMISSION_BENEFICIARIES),
  /** The payee every commission of this rule is paid to, when the beneficiary is a payee. */
  payeeId: RecordIdSchema.optional(),
  /** Of the contract price (without the maintenance deposit). */
  percent: PercentSchema,
  /** Share of the price collected before the commission may be paid; 0 pays on approval. */
  payableAtCollectedPercent: PercentSchema,
  /** Contracts dated from this day… */
  effectiveFrom: BusinessDateSchema,
  /** …to this day, inclusive; absent: open-ended. */
  effectiveTo: BusinessDateSchema.optional(),
  /** What a contract assignment does to the commission of the original sale. */
  onAssignment: z.enum(['keep', 'reverse']),
  reason: z.string().max(500),
  createdBy: z.string().max(200),
  createdAt: InstantSchema,
  retiredAt: InstantSchema.optional(),
});
export type CommissionRule = z.infer<typeof CommissionRuleSchema>;

export const SaveCommissionRuleSchema = z
  .strictObject({
    name: z.string().trim().min(2).max(120),
    legalEntityId: RecordIdSchema,
    projectIds: z.array(RecordIdSchema).max(200).default([]),
    beneficiary: z.enum(COMMISSION_BENEFICIARIES),
    payeeId: RecordIdSchema.optional(),
    percent: PercentSchema,
    payableAtCollectedPercent: PercentSchema,
    effectiveFrom: BusinessDateSchema,
    effectiveTo: BusinessDateSchema.optional(),
    onAssignment: z.enum(['keep', 'reverse']),
    reason: z.string().trim().min(3).max(500),
    /** The version being replaced; absent when creating a rule. */
    expectedVersion: z.number().int().positive().optional(),
  })
  .refine((value) => value.beneficiary !== 'payee' || value.payeeId !== undefined, {
    message: 'PAYEE_REQUIRED',
    path: ['payeeId'],
  })
  .refine((value) => !value.effectiveTo || value.effectiveTo >= value.effectiveFrom, {
    message: 'DATE_RANGE_INVALID',
    path: ['effectiveTo'],
  });
export type SaveCommissionRule = z.infer<typeof SaveCommissionRuleSchema>;

export const RetireCommissionRuleSchema = z.strictObject({
  reason: z.string().trim().min(3).max(500),
  expectedVersion: z.number().int().positive(),
});

export const CommissionSchema = z.strictObject({
  commissionId: RecordIdSchema,
  kind: z.enum(SALES_COMMISSION_KINDS),
  contractId: RecordIdSchema,
  contractNumber: z.string().max(80),
  ruleId: RecordIdSchema,
  ruleVersion: z.number().int().positive(),
  beneficiary: z.enum(COMMISSION_BENEFICIARIES),
  beneficiaryAccountId: z.string().max(200).optional(),
  payeeId: RecordIdSchema.optional(),
  /** What the percentage was applied to. */
  base: MoneySchema,
  percent: z.string().max(20),
  /** Negative for a clawback or a downward adjustment. */
  amount: MoneySchema,
  state: z.enum(SALES_COMMISSION_STATES),
  /** Why it exists or was reversed: activation, a unit replacement, a cancellation… */
  cause: z.string().max(200),
  relatedCommissionId: RecordIdSchema.optional(),
  calculatedAt: InstantSchema,
  approvedByAccountId: z.string().max(200).optional(),
  approvedAt: InstantSchema.optional(),
  paymentRequestId: RecordIdSchema.optional(),
  paidAt: InstantSchema.optional(),
  reversedAt: InstantSchema.optional(),
  reversalReason: z.string().max(500).optional(),
  recoveryReference: z.string().max(200).optional(),
  /** Collected share of the contract price now, and whether the rule's threshold is reached. */
  collectedPercent: z.string().max(20),
  payable: z.boolean(),
  legalEntityId: RecordIdSchema,
  projectId: RecordIdSchema,
  branchId: RecordIdSchema,
  version: z.number().int().positive(),
});
export type Commission = z.infer<typeof CommissionSchema>;

export const CommissionQuerySchema = z.strictObject({
  state: z.enum(SALES_COMMISSION_STATES).optional(),
  contractId: RecordIdSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});
export type CommissionQuery = z.infer<typeof CommissionQuerySchema>;

export const ApproveCommissionSchema = z.strictObject({
  expectedVersion: z.number().int().positive(),
});

export const RequestCommissionPaymentSchema = z.strictObject({
  fromAccountId: RecordIdSchema,
  method: OutgoingPaymentMethodSchema,
  /** Needed for a sales-owner commission: the payee holding the person's payment details. */
  payeeId: RecordIdSchema.optional(),
  idempotencyKey: z.string().min(8).max(200),
  expectedVersion: z.number().int().positive(),
});
export type RequestCommissionPayment = z.infer<typeof RequestCommissionPaymentSchema>;

export const RecordCommissionRecoverySchema = z.strictObject({
  reference: z.string().trim().min(2).max(200),
  expectedVersion: z.number().int().positive(),
});

export const CalculateCommissionsSchema = z.strictObject({ contractId: RecordIdSchema });

export const SALES_COMMISSION_AUDIT_ACTIONS = {
  ruleSaved: 'sales.commissionRule.saved',
  ruleRetired: 'sales.commissionRule.retired',
  calculated: 'sales.commission.calculated',
  approved: 'sales.commission.approved',
  paymentRequested: 'sales.commission.paymentRequested',
  paid: 'sales.commission.paid',
  paymentReturned: 'sales.commission.paymentReturned',
  reversed: 'sales.commission.reversed',
  recovered: 'sales.commission.recovered',
} as const;
